/**
 * Lab Market Hooks
 * Hooks for leg origin performance, market insights, and factor performance
 */

import { API_BASE_URL } from '@config/api';
import { useAuth } from '@contexts/AuthContext';
import { addDays, todayEastern } from '@parlay-picker/shared/time';
import { type ApiErrorResponse, getUserFriendlyError, safeParseJson } from '@utils/api';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  AdminMarketScope,
  DailyMarketInsight,
  FactorPerformanceRow,
  LabFilters,
  LegOriginPerformance,
  LegStatTypeByOrigin,
} from './labDashboardTypes';

// =============================================================================
// Result Types
// =============================================================================

export interface UseLegOriginPerformanceResult {
  originPerformance: LegOriginPerformance[];
  statTypeByOrigin: LegStatTypeByOrigin[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export interface UseMarketInsightsResult {
  data: DailyMarketInsight[];
  loading: boolean;
  error: string | null;
  generating: boolean;
  generateInsights: () => Promise<number>;
  refresh: () => void;
  scope: AdminMarketScope;
  setScope: (scope: AdminMarketScope) => void;
}

export interface UseFactorPerformanceResult {
  data: FactorPerformanceRow[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

// =============================================================================
// useLegOriginPerformance
// =============================================================================

/**
 * Hook to fetch leg-level origin performance data.
 * Shows hit rate breakdown by origin (AI vs Manual vs Imported Bets) at the individual pick level.
 */
export function useLegOriginPerformance(filters: LabFilters): UseLegOriginPerformanceResult {
  const { isAuthenticated, authFetch } = useAuth();
  const [originPerformance, setOriginPerformance] = useState<LegOriginPerformance[]>([]);
  const [statTypeByOrigin, setStatTypeByOrigin] = useState<LegStatTypeByOrigin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!isAuthenticated) {
      setError('Authentication required');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (filters.dateRange && filters.dateRange !== 'all') {
        params.set('dateRange', filters.dateRange);
      }
      if (filters.minSampleSize !== 5) {
        params.set('minSampleSize', filters.minSampleSize.toString());
      }

      const queryString = params.toString();

      // Fetch both endpoints in parallel
      const [originRes, statTypeRes] = await Promise.all([
        authFetch(
          `${API_BASE_URL}/api/lab/leg-origin-performance${queryString ? `?${queryString}` : ''}`,
        ),
        authFetch(
          `${API_BASE_URL}/api/lab/leg-stat-type-by-origin${queryString ? `?${queryString}` : ''}`,
        ),
      ]);

      const originResult = await safeParseJson<
        { originPerformance: LegOriginPerformance[] } | ApiErrorResponse
      >(originRes);
      const statTypeResult = await safeParseJson<
        { statTypeByOrigin: LegStatTypeByOrigin[] } | ApiErrorResponse
      >(statTypeRes);

      if (!originRes.ok) {
        const errorResult = originResult as ApiErrorResponse;
        throw new Error(getUserFriendlyError(errorResult.error));
      }
      if (!statTypeRes.ok) {
        const errorResult = statTypeResult as ApiErrorResponse;
        throw new Error(getUserFriendlyError(errorResult.error));
      }

      setOriginPerformance(
        (originResult as { originPerformance: LegOriginPerformance[] }).originPerformance,
      );
      setStatTypeByOrigin(
        (statTypeResult as { statTypeByOrigin: LegStatTypeByOrigin[] }).statTypeByOrigin,
      );
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch leg origin performance';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, authFetch, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    originPerformance,
    statTypeByOrigin,
    loading,
    error,
    refresh: fetchData,
  };
}

// =============================================================================
// useMarketInsights
// =============================================================================

export function useMarketInsights(
  limit: number = 14,
  filters?: LabFilters,
): UseMarketInsightsResult {
  const { isAuthenticated, authFetch } = useAuth();
  const [rawData, setRawData] = useState<DailyMarketInsight[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [scope, setScope] = useState<AdminMarketScope>('everything');

  const fetchInsights = useCallback(async () => {
    if (!isAuthenticated) {
      setError('Authentication required');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const url = `${API_BASE_URL}/api/lab/daily-market-insights?limit=${limit}&includeAI=true&scope=${scope}`;
      const response = await authFetch(url);
      const result = await safeParseJson<{ insights: DailyMarketInsight[] } | ApiErrorResponse>(
        response,
      );

      if (!response.ok) {
        const errorResult = result as ApiErrorResponse;
        throw new Error(getUserFriendlyError(errorResult.error));
      }

      setRawData((result as { insights: DailyMarketInsight[] }).insights);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch market insights';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, authFetch, limit, scope]);

  // Client-side date range filtering (the API returns pre-computed daily snapshots)
  const data = useMemo(() => {
    if (!filters?.dateRange || filters.dateRange === 'all') return rawData;
    const daysMatch = filters.dateRange.match(/^(\d+)d$/);
    if (!daysMatch) return rawData;
    const days = parseInt(daysMatch[1], 10);
    const cutoffStr = addDays(todayEastern(), -days);
    return rawData.filter((insight) => insight.date >= cutoffStr);
  }, [rawData, filters?.dateRange]);

  const generateInsights = useCallback(async (): Promise<number> => {
    if (!isAuthenticated) return 0;

    setGenerating(true);
    try {
      const url = `${API_BASE_URL}/api/lab/generate-market-insights?limit=${limit}`;
      const response = await authFetch(url, { method: 'POST' });
      const result = await safeParseJson<
        { success: boolean; generated: number } | ApiErrorResponse
      >(response);

      if (!response.ok) {
        const errorResult = result as ApiErrorResponse;
        throw new Error(getUserFriendlyError(errorResult.error));
      }

      const generated = (result as { success: boolean; generated: number }).generated;

      // Refresh data after generating
      if (generated > 0) {
        await fetchInsights();
      }

      return generated;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate insights';
      setError(errorMessage);
      return 0;
    } finally {
      setGenerating(false);
    }
  }, [isAuthenticated, authFetch, limit, fetchInsights]);

  useEffect(() => {
    fetchInsights();
  }, [fetchInsights]);

  return {
    data,
    loading,
    error,
    generating,
    generateInsights,
    refresh: fetchInsights,
    scope,
    setScope,
  };
}

// =============================================================================
// useFactorPerformance
// =============================================================================

export function useFactorPerformance(filters: LabFilters): UseFactorPerformanceResult {
  const { isAuthenticated, authFetch } = useAuth();
  const [data, setData] = useState<FactorPerformanceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchFactorPerformance = useCallback(async () => {
    if (!isAuthenticated) {
      setError('Authentication required');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (filters.dateRange && filters.dateRange !== 'all') {
        params.set('dateRange', filters.dateRange);
      }
      if (filters.scope && filters.scope !== 'all') {
        params.set('scope', filters.scope);
      }
      if (filters.scope === 'user' && filters.userId) {
        params.set('userId', filters.userId);
      }

      const queryString = params.toString();
      const url = `${API_BASE_URL}/api/lab/factor-performance${queryString ? `?${queryString}` : ''}`;

      const response = await authFetch(url);

      const result = await safeParseJson<
        { factorPerformance: FactorPerformanceRow[] } | ApiErrorResponse
      >(response);

      if (!response.ok) {
        const errorResult = result as ApiErrorResponse;
        throw new Error(getUserFriendlyError(errorResult.error));
      }

      setData((result as { factorPerformance: FactorPerformanceRow[] }).factorPerformance);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch factor performance';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, authFetch, filters.dateRange, filters.scope, filters.userId]);

  useEffect(() => {
    fetchFactorPerformance();
  }, [fetchFactorPerformance]);

  return { data, loading, error, refresh: fetchFactorPerformance };
}
